import { supabase } from "@/integrations/supabase/client";
import { formatDeliveryReceipt, getPrinterSettings, DeliveryReceiptData } from "@/lib/qz-tray";
import { getPrinterNameForJob } from "@/lib/print-routing";
import { dispatchPrint } from "@/lib/print-dispatcher";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

/**
 * Print the delivery driver receipt for a delivery order
 * This includes customer info, address, items, values and payment status
 */
export async function printDeliveryReceipt(
  orderId: string,
  restaurantId: string
): Promise<boolean> {
  try {
    // Print mode is handled by the dispatcher
    
    // Fetch order with all details
    const { data: order, error } = await supabase
      .from("orders")
      .select(`
        id,
        order_number,
        customer_name,
        customer_phone,
        delivery_address,
        delivery_fee,
        subtotal,
        discount,
        total,
        notes,
        status,
        order_items(id, product_name, quantity, unit_price, total_price)
      `)
      .eq("id", orderId)
      .single();
    
    if (error || !order) {
      logger.error("Error fetching order for delivery receipt:", error);
      return false;
    }
    
    if (!order.delivery_address) {
      logger.warn("Order is not a delivery order:", orderId);
      return false;
    }
    
    // Determine if order is paid based on status and payments
    const { data: payments } = await supabase
      .from("order_payments")
      .select("id, amount")
      .eq("order_id", orderId);
    
    const totalPaid = (payments || []).reduce((sum, p) => sum + Number(p.amount), 0);
    const isPaid = totalPaid >= Number(order.total);
    
    // Get printer from routing (use delivery_receipt or fallback to customer_receipt)
    const printerName = await getPrinterNameForJob(restaurantId, "delivery_receipt") 
      || await getPrinterNameForJob(restaurantId, "customer_receipt") 
      || "default";
    
    // Get printer settings
    const printerSettings = await getPrinterSettings(printerName, restaurantId);
    const charsPerLine = printerSettings?.chars_per_line || 48;
    
    const receiptData: DeliveryReceiptData = {
      orderNumber: order.order_number,
      customerName: order.customer_name || undefined,
      customerPhone: order.customer_phone || undefined,
      deliveryAddress: order.delivery_address,
      items: (order.order_items || []).map((item: any) => ({
        name: item.product_name,
        quantity: item.quantity,
        unit_price: Number(item.unit_price),
        total_price: Number(item.total_price),
      })),
      subtotal: Number(order.subtotal),
      deliveryFee: order.delivery_fee ? Number(order.delivery_fee) : undefined,
      total: Number(order.total),
      isPaid,
      orderNotes: order.notes || undefined,
    };
    
    const content = formatDeliveryReceipt(receiptData, charsPerLine);
    
    // Dispatch via centralized dispatcher
    const success = await dispatchPrint(restaurantId, printerName, content, {
      logType: "delivery",
      logIdentifier: order.order_number,
      locationName: "Via Entregador",
      orderNumber: order.order_number,
      settings: printerSettings || undefined,
    });
    
    if (success) {
      logger.log(`Delivery receipt dispatched for order #${order.order_number}`);
    }
    return success;
  } catch (err) {
    logger.error("Error printing delivery receipt:", err);
    return false;
  }
}
